(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('@yafu/curry')) :
  typeof define === 'function' && define.amd ? define(['exports', '@yafu/curry'], factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.yafu = {}, global.curry));
})(this, (function (exports, curry) { 'use strict';

  var all = curry.curry(_all);

  /**
   * Apply a predicate function to every element in a list and return `true`
   * unless at least of one of the elements yields the result `false`.
   *
   * @arg predicate {Function} The predicate function
   * @arg list {Array} The list of elements to check
   */
  function _all (predicate, list) {
    return list.length === 0 || (predicate(list[0]) === true && _all(predicate, list.slice(1)))
  }

  /**
   * Applies a binary function to the supplied arguments.
   *
   * @function callBinaryWith
   * @arg x {any} The first agument to the binary function
   * @arg y {any} The second agument to the binary function
   * @arg f {function} A unary function
   */
  function _callBinaryWith (x, y, f) {
    return f(x, y)
  }

  var callBinaryWith = curry.curry(_callBinaryWith);

  var callWith = curry.curry(_callWith);

  /**
   * Applies a unary function to the supplied argument.
   *
   * @function callWith
   * @arg x {any} The agument to the unary function
   * @arg f {function} A unary function
   */
  function _callWith (x, f) {
    return f(x)
  }

  var clamp = curry.curry(_clamp);

  /**
    * The function enables selecting a middle value
    * within a range of values between a defined minimum and maximum.
    *
    * @function clamp
    * @arg l {Number} The lower bound
    * @arg u {Number} The upper bound
    * @arg v {Number} The value that needs to be clamped
    * @return {Number}
    */
  function _clamp (l, u, v) {
    if (v > u) return u
    if (v < l) return l
    return v
  }

  var composeBinary = curry.curry(_composeBinary);

  /**
   * Composes a binary function with a unary functon. The unary function will be
   * applied to the result of the binary function.
   *
   * @arg f {Function} A unary function
   * @arg g {Function} A binary function
   * @arg x {Any} The first value to pass to the binary function
   * @arg y {Any} The second value to pass to the binary function
   */
  function _composeBinary (f, g, x, y) {
    return f(g(x, y))
  }

  var composeN = curry.curry(_composeN);

  /**
   * Composes a function of length n with a unary functon. The unary function will be
   * applied to the result of the length n function.
   *
   * @arg n {Function} The length of the second function
   * @arg f {Function} A unary function
   * @arg g {Function} A function of length n
   * @arg args {Any} n number of arguments
   */
  function _composeN (n, f, g) {
    function composed (...args) {
      return f(g(...args))
    }
    Object.defineProperty(composed, 'length', { value: n });
    return composed
  }

  var compose = curry.curry(_compose);

  /**
   * Compose two functions passing the result from the second one to the first one.
   *
   * @function compose
   * @arg f {function} The function that will apply the return value of `g`
   * @arg g {function} The function that will be apply `x` and pass the result to `f`
   * @arg x {any} The value to pass to `g`
   */
  function _compose (f, g, x) {
    return f(g(x))
  }

  function _arrayToSet (list) {
    const set = new Set();
    for (let i = 0; i < list.length; i++) {
      set.add(list[i]);
    }
    return set
  }

  var difference = curry.curry(_difference);

  /**
   * Creates a new list of unique values that are included in the first list but not in the second list
   *
   * @function difference
   * @arg {Array} list1  The first list
   * @arg {Array} list2  The second list
   * @return {Array} A list of unique elements in list1 that are not in list2
   *
   */
  function _difference (list1, list2) {
    const lookupSet = _arrayToSet(list2);
    const out = [];
    for (let i = 0; i < list1.length; i++) {
      const item = list1[i];
      if (!lookupSet.has(item)) {
        out.push(item);
        lookupSet.add(item);
      }
    }

    return out
  }

  var drop = curry.curry(_drop);

  /**
   * Remove the first n elements from a list.
   *
   * @arg n {Number} The number of elements to remove.
   * @arg list {Array} The list to remove the elements from.
   */
  function _drop (n, list) {
    return list.slice(n)
  }

  /**
   * Check for equality with using the === binary operator of props.
   */
  function eqProps (prop, a, b) {
    return a[prop] === b[prop]
  }

  var eqProps$1 = curry.curry(eqProps);

  /**
   * Check for equality with using the === binary operator.
   */
  function _eq (a, b) {
    return a === b
  }

  var eq = curry.curry(_eq);

  var findIndex = curry.curry(_findIndex);

  /**
   * Returns the index of the first item in a list that matches a predicate.
   * If no item matches, -1 is returned.
   *
   * @arg pred {function} A predicate function
   * @arg list {Array} The list to find the element in.
   */
  function _findIndex (pred, list) {
    let i;
    for (i = 0; i < list.length; i++) {
      if (pred(list[i])) {
        return i
      }
    }
    return -1
  }

  var flip = curry.curry(_flip);

  /**
   * Calls a function after flippng the order of the first two arguments
   *
   * @arg f {function} The function to call.
   * @arg x {any} The second argument to pass to the function.
   * @arg y {any} The first argument to pass to the function.
   *
   */
  function _flip (f, x, y) {
    return f(y, x)
  }

  var groupBy = curry.curry(_groupBy);

  /**
   * Returns a new object that contains all elements of the list split into
   * separate sub list. The object keys and sub-lists are determined by calling
   * the supplied grouping function on each element in the list and grouping the
   * results based on the value returned. The order of the values in the sub-list
   * are determined by the order they occur in the list.
   *
   * @function groupBy
   * @arg {Function} fn A function
   * @arg {Array} list The list to values to group
   * @return {Object} An object with keys mapped to lists of elements.
   */
  function _groupBy (fn, list) {
    const out = {};
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const key = fn(item);
      const group = out[key];
      if (group !== undefined) {
        group.push(item);
      } else {
        out[key] = [item];
      }
    }
    return out
  }

  var I = curry.curry(_I);

  /**
   * Returns the argument it is given.
   *
   * @arg x {any} The value that will be returned.
   */
  function _I (x) {
    return x
  }

  function _setToArray (set) {
    const array = [];
    set.forEach(function (item) {
      array.push(item);
    });
    return array
  }

  var intersection = curry.curry(_intersection);

  /**
   * Creates a new list of unique elements that are common to both lists using {@link http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero SameValueZero}
   * for equality comparisons.
   *
   * @function intersection
   * @arg {Array} list1  The first list
   * @arg {Array} list2  The second list
   * @return {Array} The list of elements common to both list
   *
   */
  function _intersection (list1, list2) {
    let listToIterate;
    let lookupSet;
    if (list1.length >= list2.length) {
      listToIterate = list2;
      lookupSet = _arrayToSet(list1);
    } else {
      listToIterate = list1;
      lookupSet = _arrayToSet(list2);
    }

    const out = new Set();
    for (let i = 0; i < listToIterate.length; i++) {
      const item = listToIterate[i];
      if (lookupSet.has(item)) {
        out.add(item);
      }
    }

    return _setToArray(out)
  }

  var isEmpty = curry.curry(_isEmpty);

  /**
   * Returns true if the list is empty.
   *
   * @arg list {Array} The list to check if it is empty or not.
   */
  function _isEmpty (list) {
    return list.length === 0
  }

  var k = curry.curry(_K);

  /**
   * A binary function that returns its' first argument.
   *
   * @arg x {any} The first value, will be returned.
   * @arg y {any} The second value, will be ignored.
   *
   */
  function _K (x, y) {
    return x
  }

  var map = curry.curry(_map);

  /**
   * Applies a function to every element in a list and returns a list with the results.
   *
   * @arg f {Function} The function to apply to each value in the list.
   * @arg list {array} The list of values.
   */
  function _map (f, list) {
    const out = [];
    let i;
    for (i = 0; i < list.length; ++i) {
      out.push(f(list[i]));
    }
    return out
  }

  var modArgs = curry.curry(_modArgs);

  /**
   * Call a binary function after the arguments have been modified
   * by separate transform functions.
   *
   * @function modArgs
   * @arg f {function} A binary function
   * @arg g {function} Transform function for the first argument
   * @arg h {function} Transform function for the second argument
   * @arg x {any} The first agument to the binary function
   * @arg y {any} The second agument to the binary function
   */
  function _modArgs (f, g, h, x, y) {
    return f(g(x), h(y))
  }

  /**
   * Check for non equality with using the !== binary operator.
   */
  function _neq (a, b) {
    return a !== b
  }

  var neq = curry.curry(_neq);

  var reduce = curry.curry(_reduce);

  /**
   * Reduces a list of values to a single value by passing each value to
   * a binary accumulator function along with the result of the previous iteration.
   *
   * @arg f {function} A binary accumulator function.
   * @arg x {any} The initial value, will be passed to the accumulator in the first iteration.
   * @arg list {array} The array to reduce.
   *
   */
  function _reduce (f, x, list) {
    var i;
    var acc = x;
    for (i = 0; i < list.length; ++i) {
      acc = f(acc, list[i]);
    }
    return acc
  }

  /**
   * Pass a value through a list of function where the result of each
   * call is passed to the next function in the list.
   *
   * @arg list {[Function]} The list of functions to pass the argument through.
   * @arg x {any} The initial value, will be passed to the first function in the list.
   *
   */
  const pipe = flip(reduce(callWith));

  var propEq = curry.curry(_propEq);

  /**
   * Returns true if the specified object property is strictly equal to the given value
   *
   * @arg {String} name The object property name
   * @arg {*} val The value to look for
   * @arg {Object} o The object
   */
  function _propEq (name, val, o) {
    return o[name] === val
  }

  var satisfiesAll = curry.curry(_satisfiesAll);

  /**
   * Determines if a value statisfies a list of predicates. Returns `true` unless any predicate
   * function returns `false` when applied to the value.
   *
   * @arg predicates {[Function]} The list of predicate functions to apply to the value
   * @arg val {any} The value to check
   */
  function _satisfiesAll (predicates, val) {
    return isEmpty(predicates) ||
      (predicates[0](val) === true && _satisfiesAll(drop(1, predicates), val))
  }

  /**
   * Returns a new list union of 2 lists duplicate elements. Uniqueness is determined by applying
   * the supplied function to each list element. If the supplied function produces
   * identical values for multiple list elements only the first element is kept.
   * The order of result values is determined by the order they occur in the input list.
   * {@link http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero SameValueZero} is used for equality comparisons
   *
   * @function unionBy
   * @arg {Function} fn A function to produce the value used to determine uniqueness
   * @arg {Array} list The list original
   * @arg {Array} list The list to merge
   * @return {Array} A new list of unique elements
   */

  function unionBy (fn, list1, list2) {
    const set = new Set();
    const out = [];
    for (let i = 0; i < list1.length; i++) {
      const listItem = list1[i];
      const val = fn(listItem);
      if (!set.has(val)) {
        out.push(listItem);
        set.add(val);
      }
    }

    for (let i = 0; i < list2.length; i++) {
      const listItem = list2[i];
      const val = fn(listItem);
      if (!set.has(val)) {
        out.push(listItem);
        set.add(val);
      }
    }
    return out
  }

  var unionBy$1 = curry.curry(unionBy);

  /**
   * Returns a list of unique values composed of elements from each input list using
   * {@link http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero SameValueZero} for equality comparisons.
   *
   * @function union
   * @arg {Array} list1 The first list
   * @arg {Array} list2 The second list
   * @return {Array} A new list of unique elements
   *
   */
  const union = unionBy$1(I);

  var uniqBy = curry.curry(_uniqBy);

  /**
   * Returns a new list without duplicate elements. Uniqueness is determined by applying
   * the supplied function to each list element. If the supplied function produces
   * identical values for multiple list elements only the first element is kept.
   * The order of result values is determined by the order they occur in the input list.
   * {@link http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero SameValueZero} is used for equality comparisons
   *
   * @function uniqBy
   * @arg {Function} fn A function to produce the value used to determine uniqueness
   * @arg {Array} list The list to inspect
   * @return {Array} A new list of unique elements
   */
  function _uniqBy (fn, list) {
    const set = new Set();
    const out = [];
    for (let i = 0; i < list.length; i++) {
      const listItem = list[i];
      const val = fn(listItem);
      if (!set.has(val)) {
        out.push(listItem);
        set.add(val);
      }
    }
    return out
  }

  /**
   * Returns a new list without duplicate elements.
   * {@link http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero SameValueZero} is used for equality comparisons
   *
   * @function uniq
   * @arg {Array} list The list to inspect
   * @return {Array} A new list of unique elements
   */
  const uniq = uniqBy(I);

  Object.defineProperty(exports, 'curry', {
    enumerable: true,
    get: function () { return curry.curry; }
  });
  exports.I = I;
  exports.K = k;
  exports.all = all;
  exports.callBinaryWith = callBinaryWith;
  exports.callWith = callWith;
  exports.clamp = clamp;
  exports.compose = compose;
  exports.composeBinary = composeBinary;
  exports.composeN = composeN;
  exports.difference = difference;
  exports.drop = drop;
  exports.eq = eq;
  exports.eqProps = eqProps$1;
  exports.findIndex = findIndex;
  exports.flip = flip;
  exports.groupBy = groupBy;
  exports.intersection = intersection;
  exports.isEmpty = isEmpty;
  exports.map = map;
  exports.modArgs = modArgs;
  exports.neq = neq;
  exports.pipe = pipe;
  exports.propEq = propEq;
  exports.reduce = reduce;
  exports.satisfiesAll = satisfiesAll;
  exports.union = union;
  exports.unionBy = unionBy$1;
  exports.uniq = uniq;
  exports.uniqBy = uniqBy;

  Object.defineProperty(exports, '__esModule', { value: true });

}));
//# sourceMappingURL=yafu.cjs.map
